import React from 'react';
import { Edit2, Trash2, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Note } from '../../types/note';

/**
 * Markdown renderer — security notes:
 * - react-markdown does NOT render raw HTML by default (escaped as text),
 *   so stored content can't inject markup/script
 * - its default urlTransform only allows safe protocols (http/https/mailto…),
 *   blocking javascript:/data: schemes
 * - all links open in a new tab with noopener/noreferrer
 */
const MarkdownPreview: React.FC<{ content: string }> = ({ content }) => (
    <div className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-6 [&_a]:text-blue-500 [&_a]:underline [&_a]:break-all [&_h1]:text-base [&_h1]:font-bold [&_h2]:text-sm [&_h2]:font-bold [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_code]:bg-gray-100 dark:[&_code]:bg-gray-800 [&_code]:rounded [&_code]:px-1 [&_pre]:bg-gray-100 dark:[&_pre]:bg-gray-800 [&_pre]:rounded-lg [&_pre]:p-2 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-2 [&_table]:w-full">
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                a: ({ href, children }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                        {children}
                    </a>
                ),
            }}
        >
            {content}
        </ReactMarkdown>
    </div>
);

interface NoteCardProps {
    note: Note;
    onEdit: (note: Note) => void;
    onDelete: (id: number) => void;
    /** Open the full-view reader (large notes) */
    onOpen?: (note: Note) => void;
}

export const NoteCard: React.FC<NoteCardProps> = ({ note, onEdit, onDelete, onOpen }) => {
    const isUrl = note.type === 'url';
    // List responses ship `preview` only; create/update/detail ship `content`.
    const body = note.content ?? note.preview ?? '';
    const isLarge = (note.size ?? 0) > 2048; // >2KB: open full view instead of inline

    const handleContentClick = () => {
        if (isUrl && note.content) {
            window.open(note.content, '_blank', 'noopener,noreferrer');
        } else if (isLarge && onOpen) {
            onOpen(note);
        }
    };

    const formatSize = (bytes: number) => {
        if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
        if (bytes >= 1024) return Math.round(bytes / 1024) + ' KB';
        return bytes + ' B';
    };

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    return (
        <div className="group bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all p-4 flex flex-col h-full">
            <div className="flex justify-between items-start mb-2">
                <h3 className="text-sm font-semibold text-gray-900 truncate flex-1 mr-2 leading-snug">
                    {note.title}
                </h3>
                {/* Actions: always visible on mobile, hover-only on desktop */}
                <div className="flex gap-0.5 shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={(e) => { e.stopPropagation(); onEdit(note); }}
                        className="p-2 text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors touch-manipulation"
                        aria-label="Edit note"
                    >
                        <Edit2 size={15} />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(note.id); }}
                        className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors touch-manipulation"
                        aria-label="Delete note"
                    >
                        <Trash2 size={15} />
                    </button>
                </div>
            </div>

            <div
                onClick={handleContentClick}
                className={`flex-1 mb-3 overflow-hidden ${isUrl ? 'cursor-pointer' : ''}`}
            >
                {isUrl ? (
                    <div className="flex items-start gap-1.5 text-blue-500 break-all">
                        <ExternalLink className="shrink-0 mt-0.5" size={13} />
                        <span className="text-xs underline line-clamp-3 hover:text-blue-600 transition-colors">
                            {body}
                        </span>
                    </div>
                ) : note.type === 'markdown' ? (
                    <MarkdownPreview content={body.slice(0, 700)} />
                ) : (
                    <p className="text-xs text-gray-500 line-clamp-5 whitespace-pre-wrap leading-relaxed">
                        {body}
                    </p>
                )}
            </div>

            <div className="text-[10px] text-gray-300 mt-auto pt-2 border-t border-gray-50 flex justify-between">
                <span className="uppercase tracking-wide">{isUrl ? 'Link' : note.type === 'markdown' ? 'Markdown' : 'Note'}</span>
                <span className="flex items-center gap-2">
                    {isLarge && <span className="text-blue-400">{formatSize(note.size ?? 0)}</span>}
                    <span>{formatDate(note.updated_at)}</span>
                </span>
            </div>
        </div>
    );
};
